import { type FC } from 'react';
import type { ConversationCardHeaderProps } from 'src/modules/communications/types/conversation-card.type';
import { isTestConversationDocument } from 'src/modules/communications/utils/test-conversation.util';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { TestConversationBadge } from '../../test-conversations/test-conversation-badge';

/** Renders the contact header and action slot for a conversation card. */
export const ConversationCardHeader: FC<ConversationCardHeaderProps> = ({
  conversation,
  formattedTime,
  createdTimeLabel,
  actionSlot,
  className = '',
  showTestBadge = true,
}) => {
  const showDesktopTooltip = useMediaQuery('(min-width: 640px)');
  const contact = conversation.contacts?.[0];
  const address = contact?.addresses?.[0];
  const fallbackConversationHeader = address?.address ?? 'Sin dirección';
  const contactDisplayName = formatFullName(
    contact?.firstName,
    contact?.lastName,
    fallbackConversationHeader
  );
  const isTestConversation = isTestConversationDocument(conversation);
  const timeLabel = formattedTime || createdTimeLabel;

  const titleNode = (
    <div className="min-w-0 w-full">
      <div className="flex min-w-0 items-center gap-2">
        <Text
          size="small"
          weight="semibold"
          className="truncate text-[15px] leading-[19px] text-primary-800"
          title={contactDisplayName}
        >
          {contactDisplayName}
        </Text>
        {showTestBadge && isTestConversation && (
          <div className="flex-shrink-0">
            <TestConversationBadge />
          </div>
        )}
      </div>
      {timeLabel ? (
        <Text
          size="tiny"
          color="secondary"
          className="truncate text-[11px] leading-4 text-secondary-500"
          title={timeLabel}
        >
          {timeLabel}
        </Text>
      ) : null}
    </div>
  );

  return (
    <div className={`flex items-start justify-between gap-2 ${className}`}>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="min-w-0 flex-1">
          {showDesktopTooltip ? (
            <Tooltip
              content={contactDisplayName}
              placement="top"
              size="sm"
              contentClassName="min-w-0 w-full"
            >
              {titleNode}
            </Tooltip>
          ) : (
            titleNode
          )}
        </div>
      </div>

      <div className="ml-2 flex shrink-0 items-center gap-1.5">
        {actionSlot}
      </div>
    </div>
  );
};
